import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
    selector: '[appMaxDecimals]',
    standalone: true
})
export class MaxDecimalsDirective {
  private _maxDecimals: number = 4;

  @Input() 
  set appMaxDecimals(value: string | number) {
    this._maxDecimals = typeof value === 'string' ? parseInt(value, 10) : value;
  }
  
  get appMaxDecimals(): number {
    return this._maxDecimals;
  }

  @Input() allowNegative: boolean = false;

  constructor(private el: ElementRef) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const input = this.el.nativeElement;
    const value = input.value;
    const key = event.key;
    const cursorPosition = input.selectionStart;
    const cursorEnd = input.selectionEnd;

    // Allow control and navigation keys
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 
      'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
    ];
    
    if (allowedKeys.includes(key)) {
      return;
    }
    
    // Allow Ctrl/Cmd combinations
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Allow negative sign only at the beginning if enabled
    if (key === '-' && this.allowNegative) {
      if (cursorPosition === 0 && !value.includes('-')) {
        return;
      }
      event.preventDefault();
      return;
    }

    // Only allow numbers and decimal point
    if (!/[0-9.]/.test(key)) {
      event.preventDefault();
      return;
    }

    // Handle decimal point logic
    if (key === '.') {
      if (value.includes('.')) {
        event.preventDefault();
        return;
      }
      
      if (this._maxDecimals === 0) {
        event.preventDefault();
        return;
      }
      
      return;
    }

    // Handle number input
    if (/[0-9]/.test(key)) {
      if (cursorPosition !== cursorEnd) {
        return;
      }

      if (value.includes('.')) {
        const decimalIndex = value.indexOf('.');
        
        if (cursorPosition > decimalIndex) {
          const currentDecimalPlaces = value.substring(decimalIndex + 1).length;
          
          if (currentDecimalPlaces >= this._maxDecimals) {
            event.preventDefault();
            return;
          }
        }
      }

      return;
    }

    event.preventDefault();
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    
    const pastedData = event.clipboardData?.getData('text') || '';
    const input = this.el.nativeElement;
    const currentValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const cursorEnd = input.selectionEnd || 0;

    let cleanedData = this.cleanInputValue(pastedData);
    
    const beforeCursor = currentValue.substring(0, cursorPosition);
    const afterCursor = currentValue.substring(cursorEnd);
    const newValue = beforeCursor + cleanedData + afterCursor;

    if (this.isValidValue(newValue)) {
      input.value = newValue;
      
      const inputEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(inputEvent);
      
      const newCursorPosition = cursorPosition + cleanedData.length;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }
  }

  private cleanInputValue(value: string): string {
    let cleaned = value.replace(/[^0-9.-]/g, '');
    
    if (!this.allowNegative) {
      cleaned = cleaned.replace(/-/g, '');
    } else {
      const minusIndex = cleaned.indexOf('-');
      if (minusIndex > 0) {
        cleaned = cleaned.replace(/-/g, '');
      } else if (minusIndex === 0) {
        cleaned = '-' + cleaned.substring(1).replace(/-/g, '');
      }
    }

    const decimalIndex = cleaned.indexOf('.');
    if (decimalIndex !== -1) {
      const beforeDecimal = cleaned.substring(0, decimalIndex + 1);
      const afterDecimal = cleaned.substring(decimalIndex + 1).replace(/\./g, '');
      cleaned = beforeDecimal + afterDecimal;
    }

    return cleaned;
  }

  private isValidValue(value: string): boolean {
    if (!value || value === '-') {
      return true;
    }

    const numberRegex = this.allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (!numberRegex.test(value)) {
      return false;
    }

    if (value.includes('.')) {
      const decimalPart = value.split('.')[1];
      if (decimalPart && decimalPart.length > this._maxDecimals) {
        return false;
      }
    }

    if (this._maxDecimals === 0 && value.includes('.')) {
      return false;
    }

    return true;
  }
}